import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  formatMessage,
  messages,
  SUPPORTED_LOCALES,
  type Locale,
  type MessageKey,
  type MessageParams,
} from './messages';
import { supabase } from '../lib/supabase';

export type Translate = (key: MessageKey, params?: MessageParams) => string;

interface I18nContextValue {
  locale: Locale;
  setLocale: (l: Locale) => void;
  t: Translate;
  /** Non-null when the last remote persist of the locale failed. */
  persistError: string | null;
}

const I18nContext = createContext<I18nContextValue | null>(null);

const STORAGE_KEY = 'pazportal.customer.locale';

function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

function detectInitialLocale(): Locale {
  if (typeof window === 'undefined') return 'en';
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isLocale(stored)) return stored;
  } catch {
    /* storage unavailable */
  }
  const nav = window.navigator.language?.slice(0, 2);
  return isLocale(nav) ? nav : 'en';
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectInitialLocale);
  const [persistError, setPersistError] = useState<string | null>(null);

  // Pull the persisted locale from `users.locale` once auth resolves.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data: auth } = await supabase.auth.getUser();
      if (!auth?.user) return;
      const { data } = await supabase
        .from('users')
        .select('locale')
        .eq('auth_user_id', auth.user.id)
        .maybeSingle();
      if (cancelled) return;
      const remote = data?.locale;
      if (isLocale(remote)) setLocaleState(remote);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = locale;
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    setPersistError(null);
    try {
      window.localStorage.setItem(STORAGE_KEY, next);
    } catch {
      /* per-viewer convenience only */
    }
    // Persist to users.locale (RLS lets a user update their own row).
    void supabase.auth.getUser().then(async ({ data }) => {
      if (!data?.user) return;
      const { error } = await supabase
        .from('users')
        .update({ locale: next })
        .eq('auth_user_id', data.user.id);
      if (error) setPersistError(error.message);
    });
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t: (key, params) =>
        formatMessage(messages[locale][key] ?? messages.en[key] ?? key, params),
      persistError,
    }),
    [locale, persistError, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    // Soft-fail: untranslated identity passthrough so a missing
    // provider doesn't crash the tree.
    return {
      locale: 'en',
      setLocale: () => undefined,
      t: (key, params) => formatMessage(messages.en[key] ?? key, params),
      persistError: null,
    };
  }
  return ctx;
}

export function useTranslation() {
  return useI18n();
}
